import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import AppActions from 'app/shared/flux/actions/AppActions';
import UserItemActions from 'app/shared/flux/actions/UserItemActions';
import userUtils from 'app/shared/utils/userUtils';
import { useShareAction } from 'app/shared/hooks/useShareAction';
import {
  emitTrackHideHomeFromHdpEvent,
  emitTrackReportHomeFromHdpEvent,
  emitTrackUnhideHomeFromHdpEvent,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import { useListingSaveActions } from '../../../../hooks';
import { useListingCTAs } from '../../../../hooks/useListingCTAs';
import { Button } from '../../../ui/Button';
import { FavoriteButton, ShareButton } from '../SocialActions';
import { ReportListingOverlay } from '../ListingFeedbackModule/ReportListingOverlay';
import { ListingToolsButton } from './ListingToolsButton';
import { actionLabel, actionWithLabel } from '../../../../pages/HaloListingPage/desktopStickyHeader.styles';

type ListingHeaderActionsProps = {
  size?: 'sm' | 'md';
  showCTAs?: boolean;
};

export const ListingHeaderActions = ({ size, showCTAs }: ListingHeaderActionsProps) => {
  const dispatch = useDispatch();
  const favoriteButtonRef = useRef<HTMLButtonElement>(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const { listingSaved, toggleSave } = useListingSaveActions(favoriteButtonRef);
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const isLoggedIn = useSelector((state: RootReduxState) => state.user.loggedIn);
  const userRoles = useSelector((state: RootReduxState) => state.user.info?.roles ?? []);
  const openShare = useShareAction({
    listing,
    triggerLocation: 'home_details',
    triggerObject: 'home_details_component|action_bar',
  });
  const { ctas, overlays } = useListingCTAs();
  const headerCTAs = showCTAs ? ctas.filter((c) => c.id !== 'apply') : [];
  const isRep = userUtils.isRep(userRoles) || userUtils.isAdmin(userRoles);
  const isListingHidden = Boolean(listing?.userItemTypes?.includes('hidden'));

  const handleReportListing = () => {
    emitTrackReportHomeFromHdpEvent({ triggerLocation: 'home_details' });
    setIsReportOpen(true);
  };

  const handleToggleHidden = () => {
    if (!listing) return;

    if (!isLoggedIn) {
      dispatch(
        AppActions.showPopupModal('PromptLogin', {
          caption: 'Sign in to hide listings from your search results.',
          shouldNotRedirect: true,
        }),
      );
      return;
    }

    dispatch(UserItemActions.toggleUserItem('hidden', listing));

    if (isListingHidden) {
      emitTrackUnhideHomeFromHdpEvent({ triggerLocation: 'home_details' });
    } else {
      emitTrackHideHomeFromHdpEvent({ triggerLocation: 'home_details' });
      dispatch(
        AppActions.toggleNotificationMessage({
          content: 'Listing hidden from your search results.',
        }),
      );
    }
  };

  return (
    <>
      {headerCTAs.map((cta) => (
        <div key={cta.id} className={actionWithLabel} title={cta.label}>
          <Button
            shape="circle"
            tone="neutral"
            emphasis="outlined"
            size="sm"
            onClick={cta.onClick}
            aria-label={cta.ariaLabel}
          >
            {cta.icon}
          </Button>
          <span className={actionLabel}>{cta.label}</span>
        </div>
      ))}
      <ShareButton onClick={openShare} aria-label="Share this listing" size={size} />
      <FavoriteButton
        ref={favoriteButtonRef}
        onClick={toggleSave}
        isSaved={listingSaved}
        aria-label={listingSaved ? 'Remove from saved listings' : 'Save this listing'}
        size={size}
      />
      {isRep && listing?.aliasEncoded && (
        <ListingToolsButton
          aliasEncoded={listing.aliasEncoded}
          isListingHidden={isListingHidden}
          onReportListing={handleReportListing}
          onToggleHidden={handleToggleHidden}
          size={size}
        />
      )}
      {showCTAs && overlays}
      <ReportListingOverlay open={isReportOpen} onOpenChange={setIsReportOpen} />
    </>
  );
};
